"use client";

import { Button } from "@opensea/next-ui-kit";
import { Flex, FlexColumn } from "@opensea/ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
const BEST_SCORE_KEY = "os2_offline_best_score";

// Canvas logical size; we scale by devicePixelRatio for crispness
const LOGICAL_WIDTH = 960;
const LOGICAL_HEIGHT = 360;
function clamp(min, value, max) {
  return Math.max(min, Math.min(value, max));
}
function nowMs() {
  return Date.now();
}
function aabbCollides(a, b) {
  // Strict overlap (edge touch is NOT a collision)
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
export function SeaRunner({
  message
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(nowMs());
  const [state, setState] = useState("running");
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const currentScoreRef = useRef(0);

  // World config
  const {
    groundY,
    gravity,
    jumpVelocity,
    startSpeed,
    maxSpeed
  } = useMemo(() => ({
    groundY: LOGICAL_HEIGHT - 52,
    gravity: 0.8,
    jumpVelocity: -12,
    startSpeed: 6,
    maxSpeed: 14
  }), []);
  const speedRef = useRef(startSpeed);
  const playerRef = useRef({
    x: 60,
    y: groundY - 28,
    w: 46,
    h: 28,
    vy: 0,
    onGround: true,
    blinkAt: nowMs() + 1500 + Math.random() * 2000,
    bobToggleAt: nowMs() + 150,
    bobUp: false
  });
  const obstaclesRef = useRef([]);
  const pickupsRef = useRef([]);
  const obstacleSpawnAtRef = useRef(nowMs() + 1200);
  const pickupSpawnAtRef = useRef(nowMs() + 1700);

  // Load best score
  useEffect(() => {
    try {
      const raw = localStorage.getItem(BEST_SCORE_KEY);
      if (raw) {
        setBest(Number(raw) || 0);
      }
    } catch {
      // ignore
    }
  }, []);
  const reset = useCallback(() => {
    setScore(0);
    currentScoreRef.current = 0;
    speedRef.current = startSpeed;
    setState("running");
    playerRef.current = {
      x: 60,
      y: groundY - 28,
      w: 46,
      h: 28,
      vy: 0,
      onGround: true,
      blinkAt: nowMs() + 1500 + Math.random() * 2000,
      bobToggleAt: nowMs() + 150,
      bobUp: false
    };
    obstaclesRef.current = [];
    pickupsRef.current = [];
    obstacleSpawnAtRef.current = nowMs() + 1200;
    pickupSpawnAtRef.current = nowMs() + 1700;
  }, [groundY, startSpeed]);
  const jump = useCallback(() => {
    if (state !== "running") {
      return;
    }
    const p = playerRef.current;
    if (p.onGround) {
      p.vy = jumpVelocity;
      p.onGround = false;
    }
  }, [jumpVelocity, state]);

  // Controls
  useEffect(() => {
    const onKey = e => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        jump();
      } else if (e.key.toLowerCase() === "r") {
        e.preventDefault();
        reset();
      }
    };
    window.addEventListener("keydown", onKey, {
      passive: false
    });
    const canvas = canvasRef.current;
    const onPointer = () => jump();
    canvas?.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      canvas?.removeEventListener("pointerdown", onPointer);
    };
  }, [jump, reset]);

  // Resize canvas to device pixel ratio
  useEffect(() => {
    const canvas_0 = canvasRef.current;
    if (!canvas_0) {
      return;
    }
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    canvas_0.width = Math.floor(LOGICAL_WIDTH * dpr);
    canvas_0.height = Math.floor(LOGICAL_HEIGHT * dpr);
    canvas_0.style.width = `${LOGICAL_WIDTH}px`;
    canvas_0.style.height = `${LOGICAL_HEIGHT}px`;
    const ctx = canvas_0.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);
  const endGame = useCallback(finalScore => {
    setState("gameover");
    setBest(prev => {
      const next = Math.max(prev, finalScore);
      try {
        localStorage.setItem(BEST_SCORE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);
  const drawBackground = ctx_0 => {
    // Sky
    ctx_0.fillStyle = "#ffffff";
    ctx_0.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    // Sea
    ctx_0.fillStyle = "#f2f6ff";
    ctx_0.fillRect(0, groundY + 20, LOGICAL_WIDTH, LOGICAL_HEIGHT - (groundY + 20));

    // Ground line (waterline)
    ctx_0.strokeStyle = "#000000";
    ctx_0.lineWidth = 3;
    ctx_0.beginPath();
    ctx_0.moveTo(0, groundY);
    ctx_0.lineTo(LOGICAL_WIDTH, groundY);
    ctx_0.stroke();
  };
  const drawPlayer = (ctx_1, p_0) => {
    // Simple sailboat: hull + mast + sail + cabin window (eyes)
    const x = p_0.x;
    const y = p_0.y;
    const w = p_0.w;
    const h = p_0.h;

    // Hull
    ctx_1.fillStyle = "#000000";
    ctx_1.beginPath();
    ctx_1.moveTo(x, y + h - 2);
    ctx_1.lineTo(x + w, y + h - 2);
    ctx_1.lineTo(x + w - 8, y + h - 10);
    ctx_1.lineTo(x + 8, y + h - 10);
    ctx_1.closePath();
    ctx_1.fill();

    // Mast
    ctx_1.fillRect(x + w / 2 - 1, y + 4, 2, h - 12);

    // Sail (simple triangle with slight bob two-frame)
    const sailOffset = p_0.onGround && p_0.bobUp ? -2 : 0;
    ctx_1.fillStyle = "#000000";
    ctx_1.beginPath();
    ctx_1.moveTo(x + w / 2, y + 6 + sailOffset);
    ctx_1.lineTo(x + w / 2, y + h - 12 + sailOffset);
    ctx_1.lineTo(x + w - 6, y + h - 12 + sailOffset);
    ctx_1.closePath();
    ctx_1.fill();

    // Cabin window as two little eyes that blink
    const blinkVisible = playerRef.current.onGround && nowMs() < playerRef.current.blinkAt + 120;
    ctx_1.fillStyle = blinkVisible ? "#000000" : "#ffffff";
    ctx_1.fillRect(x + 10, y + h - 16, 3, 3);
    ctx_1.fillRect(x + 15, y + h - 16, 3, 3);
  };
  const drawObstacle = (ctx_2, o) => {
    ctx_2.fillStyle = "#000000";
    if (o.type === "barrel") {
      ctx_2.fillRect(o.x, o.y, o.w, o.h);
    } else if (o.type === "wave" || o.type === "large-wave") {
      // Triangle wave
      ctx_2.beginPath();
      ctx_2.moveTo(o.x, o.y + o.h);
      ctx_2.lineTo(o.x + o.w / 2, o.y);
      ctx_2.lineTo(o.x + o.w, o.y + o.h);
      ctx_2.closePath();
      ctx_2.fill();
    } else if (o.type === "monster") {
      // Sea monster bump
      ctx_2.beginPath();
      ctx_2.arc(o.x + o.w / 2, o.y + o.h, o.w / 2, Math.PI, 0);
      ctx_2.fill();
    } else if (o.type === "squid") {
      // Tentacle
      ctx_2.fillRect(o.x + o.w / 2 - 2, o.y, 4, o.h);
      ctx_2.beginPath();
      ctx_2.arc(o.x + o.w / 2, o.y, 6, 0, Math.PI, true);
      ctx_2.fill();
    }
  };
  const drawPickup = (ctx_3, p_1) => {
    ctx_3.fillStyle = "#000000";
    if (p_1.type === "pearl") {
      ctx_3.beginPath();
      ctx_3.arc(p_1.x + p_1.w / 2, p_1.y + p_1.h / 2, Math.min(p_1.w, p_1.h) / 2, 0, Math.PI * 2);
      ctx_3.fill();
    } else if (p_1.type === "shell") {
      ctx_3.beginPath();
      ctx_3.moveTo(p_1.x, p_1.y + p_1.h);
      ctx_3.lineTo(p_1.x + p_1.w, p_1.y + p_1.h);
      ctx_3.lineTo(p_1.x + p_1.w / 2, p_1.y);
      ctx_3.closePath();
      ctx_3.fill();
    } else if (p_1.type === "chest") {
      ctx_3.fillRect(p_1.x, p_1.y + p_1.h / 4, p_1.w, p_1.h * 3 / 4);
    } else if (p_1.type === "bottle") {
      ctx_3.fillRect(p_1.x + p_1.w / 2 - 2, p_1.y, 4, p_1.h - 6);
      ctx_3.fillRect(p_1.x + 3, p_1.y + p_1.h - 6, p_1.w - 6, 6);
    }
  };
  const update = dtMs => {
    const p_2 = playerRef.current;
    const speed = speedRef.current;
    const dt = dtMs / 16.6667; // normalize to 60fps units

    // Speed ramp
    speedRef.current = clamp(startSpeed, speed + 0.0009 * dtMs, maxSpeed);

    // Player physics
    p_2.vy += gravity * dt;
    p_2.y += p_2.vy;
    if (p_2.y + p_2.h >= groundY) {
      p_2.y = groundY - p_2.h;
      p_2.vy = 0;
      p_2.onGround = true;
    } else {
      p_2.onGround = false;
    }

    // Blink scheduling
    if (nowMs() > p_2.blinkAt + 180) {
      p_2.blinkAt = nowMs() + 1800 + Math.random() * 2600;
    }
    // Bobbing while grounded
    if (p_2.onGround && nowMs() > p_2.bobToggleAt) {
      p_2.bobToggleAt = nowMs() + 150;
      p_2.bobUp = !p_2.bobUp;
    }

    // Spawn obstacles
    if (nowMs() > obstacleSpawnAtRef.current) {
      const roll = Math.random();
      const largeUnlocked = score > 500;
      const type = roll < 0.35 ? "barrel" : roll < 0.6 ? "wave" : roll < 0.8 ? "monster" : largeUnlocked && roll > 0.9 ? "large-wave" : "squid";
      const baseHeight = type === "barrel" ? 22 : type === "wave" ? 34 : type === "monster" ? 40 : type === "large-wave" ? 60 : 46;
      const h_0 = baseHeight;
      const w_0 = type === "barrel" ? 18 : type === "wave" ? 26 : type === "large-wave" ? 38 : 22;
      obstaclesRef.current.push({
        type,
        x: LOGICAL_WIDTH + 10,
        y: groundY - h_0,
        w: w_0,
        h: h_0,
        speed: speed + (Math.random() * 1.5 - 0.5)
      });
      const minDelay = 550;
      const maxDelay = 1200;
      const delay = clamp(350, maxDelay - speed * 30 + Math.random() * 300, maxDelay);
      obstacleSpawnAtRef.current = nowMs() + clamp(minDelay, delay, maxDelay);
    }

    // Spawn pickups occasionally
    if (nowMs() > pickupSpawnAtRef.current) {
      const type_0 = ["pearl", "shell", "chest", "bottle"][Math.floor(Math.random() * 4)];
      const w_1 = 16;
      const h_1 = 16;
      const altitude = groundY - 60 - Math.random() * 60;
      pickupsRef.current.push({
        type: type_0,
        x: LOGICAL_WIDTH + 10,
        y: altitude,
        w: w_1,
        h: h_1,
        speed: speed - 0.5
      });
      pickupSpawnAtRef.current = nowMs() + 2200 + Math.random() * 1800;
    }

    // Move and cull obstacles
    obstaclesRef.current = obstaclesRef.current.map(o_0 => ({
      ...o_0,
      x: o_0.x - o_0.speed
    })).filter(o_1 => o_1.x + o_1.w > -40);

    // Move and cull pickups
    pickupsRef.current = pickupsRef.current.map(pu => ({
      ...pu,
      x: pu.x - pu.speed
    })).filter(pu_0 => pu_0.x + pu_0.w > -40);

    // Collect pickups first
    let collected = 0;
    pickupsRef.current = pickupsRef.current.filter(pu_1 => {
      if (aabbCollides(playerRef.current, pu_1)) {
        collected += 25;
        return false;
      }
      return true;
    });

    // Calculate and update score
    const scoreIncrement = Math.floor(1 + dt * (0.2 + speed * 0.02)) + collected;
    currentScoreRef.current += scoreIncrement;
    setScore(currentScoreRef.current);

    // Check collisions after score update
    for (const o_2 of obstaclesRef.current) {
      if (aabbCollides(playerRef.current, o_2)) {
        endGame(currentScoreRef.current);
        break;
      }
    }
  };
  const render = ctx_4 => {
    drawBackground(ctx_4);

    // Entities
    drawPlayer(ctx_4, playerRef.current);
    for (const o_3 of obstaclesRef.current) {
      drawObstacle(ctx_4, o_3);
    }
    for (const pu_2 of pickupsRef.current) {
      drawPickup(ctx_4, pu_2);
    }

    // HUD
    ctx_4.fillStyle = "#000000";
    ctx_4.font = "16px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx_4.fillText(`Score: ${score}`, 16, 28);
    ctx_4.fillText(`Best: ${best}`, LOGICAL_WIDTH - 120, 28);
  };
  useEffect(() => {
    const canvas_1 = canvasRef.current;
    if (!canvas_1) {
      return;
    }
    const ctx_5 = canvas_1.getContext("2d");
    const loop = () => {
      const now = nowMs();
      const dt_0 = now - lastTimeRef.current;
      lastTimeRef.current = now;
      if (state === "running") {
        update(dt_0);
      }
      render(ctx_5);
      rafRef.current = window.requestAnimationFrame(loop);
    };
    rafRef.current = window.requestAnimationFrame(loop);
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [best, score, state]);
  return <FlexColumn className="w-full">
      <div className="mb-3 text-center">
        <h2 className="font-bold text-2xl">OpenSea is offline</h2>
        {message ? <p className="mt-1 text-base">{message}</p> : <p className="mt-1 text-sm">
            Mono Runner — avoid obstacles, rack up points. Refresh anytime to
            try loading OpenSea.
          </p>}
      </div>

      <div className="mx-auto max-w-[960px] rounded-xl border border-black/80 p-2">
        <div className="relative">
          <canvas aria-label="Endless runner mini-game canvas" className="block w-full cursor-pointer touch-none select-none bg-white" ref={canvasRef} />

          {state === "gameover" && <div className="absolute inset-0 flex items-center justify-center bg-white/80">
              <div className="text-center">
                <div className="mb-2 font-semibold text-lg">Game over</div>
                <div className="mb-4 text-sm">
                  Score {score} • Best {best}
                </div>
                <Flex className="items-center justify-center gap-3">
                  <Button onClick={reset} variant="secondary">
                    Play again (R)
                  </Button>
                  <Button onClick={() => window.location.reload()} variant="primary">
                    Reload OpenSea
                  </Button>
                </Flex>
              </div>
            </div>}
        </div>

        <div className="mt-2 text-center text-xs">
          Space/↑ to jump · Tap/click to jump
        </div>
      </div>
    </FlexColumn>;
}
export default SeaRunner;